import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { MaintenanceWindowData, MaintenanceWindowFormData, MaintenanceWindowTypeData } from '../types';
import { WEB_COMPONENT_MAINTENANCE_WINDOW_FORM_PATCH_SPECIFICS } from '../../../webcomponents.constants';
import { FormGroup } from '../../../ui/gzForm/formGroup/form-group';
import { Subscription } from 'rxjs';
import { htmlEncode } from '../../../utils/escape.utils';
import { RequiredValidator } from '../../../ui/gzForm/validators/required.validator';
import { TemplateState } from '../../../types/common/types';
import { MinLengthValidator } from '../../../ui/gzForm/validators/min-length.validator';
import { MaxLengthValidator } from '../../../ui/gzForm/validators/max-length.validator';
import { RemoteValidator } from '../../../ui/gzForm/validators/remote.validator';
import { MaintenanceWindowsConstants } from '../../../maintenanceWindows/MaintenanceWindowsConstants';
import { MaintenanceWindowTypes } from '../../../types/common/maintenanceWindowTypes';
import { InputEvents } from '../../../ui/gzInput/types';
import {Rights} from "@nirvana/shared/constants";

export class MaintenanceWindowFormView extends BaseWebComponentView {
  public formGroup!: FormGroup;
  private formGroupSubscription: Subscription | null = null;
  public maintenanceWindow: MaintenanceWindowData;

  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$loadingErrorMessage = this.shadow.querySelector('.loading-error--message');
    this.dom.$formWrapper = this.shadow.querySelector('.form-wrapper');
    this.dom.$title = this.shadow.querySelector('#maintenance-type-title');
    // Form fields
    this.dom.$windowNameFormField = this.shadow.querySelector('#window-name-form-field');
    this.dom.$type = this.shadow.querySelector('#maintenance-window-type-form-field');
    this.dom.$allowChangeByOtherUsersFormField = this.shadow.querySelector('#others-can-make-changes-form-field');
    this.dom.$patchSpecificsComponent = this.shadow.querySelector(
      WEB_COMPONENT_MAINTENANCE_WINDOW_FORM_PATCH_SPECIFICS
    );

    this.dom.$btnSave = this.shadow.getElementById('btnSave');
    this.dom.$btnCancel = this.shadow.getElementById('btnCancel');

    this.setLoadingState();

    this.setTranslatedStrings();
    this.injectDependenciesInChildren();

    this.dom.$windowNameFormField.Input.addEventListener(InputEvents.BLUR, () => {
      this.dom.$windowNameFormField.Input.setFormValue(this.dom.$windowNameFormField.Input.getFormValue().trim());
    });
  }

  setTranslatedStrings() {
    this.dom.$title.innerText = htmlEncode(this.dependencies.translateFn('Maintenance type'));
    this.dom.$btnSave.innerHTML = this.commonStrings.save;
    this.dom.$btnCancel.innerHTML = this.commonStrings.cancel;
    this.dom.$loadingErrorMessage.innerHTML = this.commonStrings.generalSystemError;

    this.dom.$windowNameFormField.label = htmlEncode(this.dependencies.translateFn('Window name'));
    this.dom.$type.label = htmlEncode(this.dependencies.translateFn('Type'));
    this.dom.$allowChangeByOtherUsersFormField.Input.innerText = this.dependencies.translateFn(
      'Allow others to make changes to this maintenance window'
    );
  }

  private injectDependenciesInChildren() {
    if (this.dom.$patchSpecificsComponent.injectDependencies) {
      this.dom.$patchSpecificsComponent.injectDependencies(this.dependencies);
      this.dom.$patchSpecificsComponent.start().catch(console.error);
    }
  }

  public setLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.LOADING);
  }

  public removeLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
  }

  buildSuccessView(data: MaintenanceWindowFormData) {
    this.maintenanceWindow = data.maintenanceWindowData;

    this.createFormGroup(data.maintenanceWindowType);
    this.setMaintenanceWindowTypeDropdownOptions(data.maintenanceWindowsTypes);
    this.setDisabledInnerComponents(false);
    this.setFormData(data);

    // enable / disable "Save" button based on formGroup's validity
    this.formGroupSubscription = this.formGroup.valid$.subscribe({
      next: valid => {
        this.dom.$btnSave.disabled = !valid;
      },
    });

    this.dom.$patchSpecificsComponent.dispatchEvent(
      new CustomEvent('computeFormState', {
        detail: {},
        bubbles: true,
      })
    );

    if (
      this.maintenanceWindow.allowChangeByOtherUsers !== undefined &&
      !this.maintenanceWindow.allowChangeByOtherUsers &&
      !(data.currentUserIsOwner ?? false)
    ) {
      // disable form
      this.setDisabledInnerComponents(true);
    }

    if (!Application.Main.hasAllRights([Rights.MANAGE_POLICIES_WRITE])) {
      this.setDisabledInnerComponents(true);
    }

    this.removeLoadingState();
  }

  private createFormGroup(maintenanceWindowType: number) {
    // create specifics depending on maintenanceWindowType
    if (maintenanceWindowType.toString() === MaintenanceWindowTypes.PATCH_MANAGEMENT.toString()) {
      this.createPatchManagementFormGroup();
    }
  }

  private createPatchManagementFormGroup() {
    this.dom.$windowNameFormField.Validators = [
      RequiredValidator(this.dependencies.translateFn('Specify a maintenance window name.')),
      MinLengthValidator(
        MaintenanceWindowsConstants.MaintenanceWindowName.MinLength,
        this.dependencies.translateFn('The name must have minimum {maintenanceWindowsMinLength} characters.', {
          maintenanceWindowsMinLength: MaintenanceWindowsConstants.MaintenanceWindowName.MinLength,
        })
      ),
      MaxLengthValidator(
        MaintenanceWindowsConstants.MaintenanceWindowName.MaxLength,
        this.dependencies.translateFn('The name must have maximum {maintenanceWindowsMaxLength} characters.', {
          maintenanceWindowsMaxLength: MaintenanceWindowsConstants.MaintenanceWindowName.MaxLength,
        })
      ),
      RemoteValidator(
        (value: string) =>
          this.dependencies.api.clients.ObservableClients.MaintenanceWindows.checkNameIsUnique({
            maintenanceWindowName: value,
            companyId: this.dependencies.getActiveCompany().companyId,
            maintenanceWindowId: this.maintenanceWindow?._id,
          }),
        (response: { success: boolean }) => response.success,
        this.dependencies.translateFn(
          'A maintenance window with the same name already exists. Please specify a different name.'
        ),
        this.dependencies.translateFn(
          'A communication error has occurred. Please enter the maintenance window name again.'
        )
      ),
    ];

    this.formGroup = new FormGroup({
      name: this.dom.$windowNameFormField,
      type: this.dom.$type,
      allowChangeByOtherUsers: this.dom.$allowChangeByOtherUsersFormField,
      specifics: this.dom.$patchSpecificsComponent.controller?.view.getFormGroup(),
    });
  }

  private setMaintenanceWindowTypeDropdownOptions(types: MaintenanceWindowTypeData[]) {
    this.dom.$type.Input.options = types.map(type => {
      return {
        title: type.name,
        value: type.value.toString(),
      };
    });
  }

  private setFormData(data: MaintenanceWindowFormData) {
    // common data for add/edit
    this.dom.$type.Input.setFormValue(data.maintenanceWindowType.toString());
    this.dom.$windowNameFormField.Input.setFormValue(data.maintenanceWindowData?.name);

    if (data.maintenanceWindowData?._id) {
      // on edit mode, type cannot be changed
      this.dom.$type.disabled = true;
      this.dom.$allowChangeByOtherUsersFormField.Input.setFormValue(data.maintenanceWindowData.allowChangeByOtherUsers);
      this.dom.$patchSpecificsComponent.viewData = {
        isEditMode: true,
        maintenanceWindowPatchSpecificsData: data.maintenanceWindowData.specifics,
      };
    } else {
      this.setDefaultFormData();
    }
  }

  setDisabledInnerComponents(disable: boolean) {
    const disableableComponents = this.getDisableableComponents();
    disableableComponents.forEach(disableableComponent => {
      disableableComponent.disabled = disable;
    });
    if (disable) {
      // no need for Save button to be enabled when the entire form is read-only
      this.dom.$btnSave.disabled = true;
    }
  }

  private getDisableableComponents(): any[] {
    return [
      this.dom.$windowNameFormField,
      this.dom.$allowChangeByOtherUsersFormField,
      this.dom.$patchSpecificsComponent,
    ];
  }

  private async setDefaultFormData() {
    this.dom.$allowChangeByOtherUsersFormField.Input.setFormValue(false);
    await this.dom.$patchSpecificsComponent.resetViewData();
  }

  public async resetFormToDefault() {
    this.dom.$type.Input.setFormValue('');
    this.dom.$windowNameFormField.Input.setFormValue('');
    await this.setDefaultFormData();
    this.setDisabledInnerComponents(false);
    this.dom.$type.disabled = false;
    this.dom.$btnSave.disabled = true;
    // necessary for cases when the backend call remains hung and does not throw exception to be caught in web components,
    // so the loading spinner remains set on the button
    this.dom.$btnSave.showSpinner = false;
  }

  buildErrorView(error: Error) {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  protected onDisconnect() {
    this.unsubscribeObservers();
  }

  unsubscribeObservers() {
    this.formGroup?.unsubscribe();
    this.formGroupSubscription?.unsubscribe();
  }
}
